import React, { useEffect } from "react";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import styled from "styled-components";
import SpaceBetween from "../DefaultStyles/SpaceBetween";
import Flex from "../DefaultStyles/Flex";
import ConfirmBtn from "../Button/ConfirmBtn";
import useMy from "../../hooks/useMy";
import { atom, useRecoilState, useSetRecoilState } from "recoil";
import ToolTip from "./ToolTip";

interface SModalContent {
  isSmallModal?: boolean;
  hasDesc?: boolean;
  isMediumModal?: boolean;
  isLargeModal?: boolean;
  isOverflowVisible?: boolean;
  contentPadding?: string;
}

interface DefaultModalProps extends SModalContent {
  modalTitle?: string;
  children?: JSX.Element | JSX.Element[];
  closeEvent: () => void;
  confirmEvent: () => void;
  deleteBtnClickEvent?: () => void;
  deleteBtnShow?: boolean;
  confirmDisabled?: boolean;
  type?: "view" | "edit" | "input";
  isDeleteModal?: boolean;
  isLargeModal?: boolean;
  modalDesc?: string;
  confirmBtnText?: string;
  leftBottomBtn?: JSX.Element;
  leftBottomTime?: string;
  leftBottomName?: string;
  leftBottomTitle?: string;
  cancelBtn?: JSX.Element;
  cancelBtnText?: string;
  cancelBtnDisabled?: boolean;
  backBtn?: boolean;
  backBtnEvent?: () => void;
  checkScrollHeight?: boolean;
  changingEditEvent?: () => void;
  contentPadding?: string;
  refreshBtnShow?: boolean;
  refreshBtnClickEvent?: () => void;
  confirmBtnColor?: string;
}

export const defaultModalType = atom<string | undefined>({
  key: "defaultModalType",
  default: undefined,
});

export const defaultModalScrollHeight = atom<number>({
  key: "defaultModalScrollHeight",
  default: 0,
});

export default function DefaultModalLayout({
  modalTitle,
  children,
  closeEvent,
  confirmEvent,
  confirmDisabled,
  deleteBtnClickEvent,
  type = "input",
  isSmallModal,
  cancelBtnText,
  isDeleteModal,
  modalDesc,
  confirmBtnText,
  leftBottomBtn,
  leftBottomTime,
  leftBottomName,
  leftBottomTitle,
  cancelBtn,
  isMediumModal,
  isLargeModal,
  isOverflowVisible,
  deleteBtnShow,
  cancelBtnDisabled,
  backBtn,
  backBtnEvent,
  checkScrollHeight,
  changingEditEvent,
  contentPadding,
  refreshBtnShow,
  refreshBtnClickEvent,
  confirmBtnColor,
}: DefaultModalProps) {
  const [modalType, setModalType] = useRecoilState(defaultModalType ?? type);
  const setModalScrollHeight = useSetRecoilState(defaultModalScrollHeight);

  const { name } = useMy();
  dayjs.locale("ko");

  useEffect(() => {}, [modalType]);

  useEffect(() => {
    setModalType(type);
  }, [type]);

  return (
    <Overlay>
      <ModalWrapper isLargeModal={isLargeModal}>
        <ModalContainer>
          <SpaceBetween>
            <div>
              <TitleCotainer>
                <>
                  {backBtn && (
                    <BackBtn
                      onClick={() => {
                        if (backBtnEvent) {
                          backBtnEvent();
                        }
                      }}
                    >
                      <span className="material-icons-outlined">
                        chevron_left
                      </span>
                    </BackBtn>
                  )}
                </>
                <ModalTitle>{modalTitle}</ModalTitle>
              </TitleCotainer>
              {modalDesc && <ModalDesc>{modalDesc}</ModalDesc>}
            </div>

            {deleteBtnShow ? (
              <DeleteIcon
                className="material-icons-outlined"
                onClick={() => {
                  if (deleteBtnClickEvent) {
                    deleteBtnClickEvent();
                  }
                }}
              >
                delete
              </DeleteIcon>
            ) : refreshBtnShow ? (
              <DeleteIcon
                className="material-icons-outlined"
                style={{ color: "#aaafb9" }}
                onClick={() => {
                  if (refreshBtnClickEvent) {
                    refreshBtnClickEvent();
                  }
                }}
              >
                refresh
              </DeleteIcon>
            ) : (
              <></>
            )}
          </SpaceBetween>

          <ModalContent
            id="modal-content"
            isSmallModal={isSmallModal}
            isMediumModal={isMediumModal}
            isLargeModal={isLargeModal}
            hasDesc={modalDesc ? true : false}
            contentPadding={contentPadding}
            isOverflowVisible={isOverflowVisible}
            onScroll={() => {
              if (checkScrollHeight) {
                setModalScrollHeight(
                  document.getElementById("modal-content")?.scrollTop ?? 0
                );
              }
            }}
          >
            {children}
          </ModalContent>
        </ModalContainer>

        <ModalFooter>
          <SpaceBetween>
            <>
              <CheckDateContainer>
                {leftBottomBtn ?? (
                  <>
                    <CheckDateText>
                      {dayjs(leftBottomTime ?? undefined).format(
                        "YYYY.MM.DD (dd)"
                      )}
                    </CheckDateText>
                    <CheckDateText>
                      {leftBottomTitle ?? "관리자"}: {leftBottomName ?? name}
                    </CheckDateText>
                  </>
                )}
              </CheckDateContainer>

              <Flex gapSize={8}>
                {cancelBtn ?? (
                  <Flex gapSize={4}>
                    <>
                      {cancelBtnDisabled && (
                        <ToolTip
                          msg="현재 등급은 본인이 작성한 내용만 수정 및 삭제가 가능합니다."
                          iconSize={18}
                          direction="top"
                        />
                      )}
                    </>
                    <ConfirmBtn
                      btnText={
                        cancelBtnText ??
                        (isDeleteModal
                          ? "취소"
                          : modalType === "view"
                          ? "수정"
                          : "닫기")
                      }
                      btnColor="white"
                      btnClickEvent={() => {
                        if (modalType === "view" && !isDeleteModal) {
                          setModalType("edit");

                          if (changingEditEvent) {
                            changingEditEvent();
                          }
                        } else {
                          closeEvent();
                        }
                      }}
                      isDisabled={cancelBtnDisabled ?? false}
                    />
                  </Flex>
                )}
                <ConfirmBtn
                  btnText={
                    isDeleteModal
                      ? confirmBtnText ?? "완료"
                      : modalType === "view"
                      ? "닫기"
                      : confirmBtnText ?? "확인"
                  }
                  btnColor={confirmBtnColor ?? (isDeleteModal ? "red" : "mint")}
                  isDisabled={confirmDisabled}
                  btnClickEvent={() => {
                    console.log("modalType", modalType);

                    if (modalType === "view" && !isDeleteModal) {
                      closeEvent();
                    } else {
                      confirmEvent();
                    }
                  }}
                />
              </Flex>
            </>
          </SpaceBetween>
        </ModalFooter>
      </ModalWrapper>
    </Overlay>
  );
}

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.25);

  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
`;

const ModalWrapper = styled.div<{ isLargeModal?: boolean }>`
  max-width: ${(props) => (props.isLargeModal ? "650px" : "500px")};
  width: 100%;
  height: auto;
  background-color: ${({ theme }) => theme.color.white};
  border-radius: 15px;

  z-index: 100;
`;

export const ModalContainer = styled.div`
  width: 100% !important;
  display: flex;
  flex-direction: column;

  padding-top: 36px;
`;

export const ModalTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  line-height: 39px;
  text-align: left;
  color: ${({ theme }) => theme.color.gray800};
`;

export const ModalDesc = styled.div`
  padding-left: 32px;
  margin-top: 12px;
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  text-align: left;
  color: ${({ theme }) => theme.buttonColor.linePressed};
  white-space: pre-line;
`;

const ModalContent = styled.div<SModalContent>`
  width: 100% !important;
  display: flex;
  flex-direction: column;

  height: ${({ isSmallModal, isMediumModal }) =>
    isMediumModal
      ? "calc(440px - 77px - 39px - 36px)"
      : isSmallModal
      ? "calc(336px - 77px - 39px - 36px)"
      : "calc(660px - 77px - 39px - 36px)"};
  max-height: calc(100vh - 200px);
  overflow-x: ${({ isOverflowVisible }) =>
    isOverflowVisible ? "visible" : "hidden"};
  padding: ${({ hasDesc, contentPadding }) =>
    contentPadding ?? (hasDesc ? "24px 32px 0 32px" : "40px 32px 0 32px")};
  overflow-y: auto;

  &::-webkit-scrollbar {
    background-color: #fff;
    width: 16px;
    padding-top: 20px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #babac0;
    border-radius: 16px;
    border: 5px solid #fff;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #a0a0a5;
    border: 4px solid #f4f4f4;
  }

  &::-webkit-scrollbar-track:hover {
    background-color: #f4f4f4;
  }
`;

const ModalFooter = styled.footer`
  width: 100%;
  border-top: 1px solid #dfe3e9;
  padding: 18px 32px;
`;

const CheckDateContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CheckDateText = styled.span`
  font-size: 13px;
  line-height: 19.5px;
  text-align: left;
  color: #9a9fa8;
`;

const DeleteIcon = styled.span`
  cursor: pointer;
  margin-right: 32px;
  width: 38px;
  height: 38px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #858c97;

  &:hover {
    background-color: #f5f5f5;
    color: #f65745;
  }
`;

const TitleCotainer = styled.div`
  display: flex;
  align-items: center;
  padding-left: 32px;
  gap: 10px;
`;

const BackBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    color: #000000 !important;
  }

  &:hover {
    background-color: #ebecf0;
  }
`;

export const ModalFixedBox = styled.div`
  width: 100%;
  border-radius: 3px;
  background-color: #f6f6f8;
  padding: 10px 16px;

  font-size: 14px;
  font-weight: bold;
  line-height: 21px;
  text-align: left;
  color: #808080;
`;
